import { useEffect, useState } from "react";
import {
  Card,
  Spin,
  Row,
  Col,
  Tag,
  Typography,
  Divider,
  message,
  Empty,
  Space,
  Statistic,
  Table,
  Avatar,
  Progress,
  Tabs,
  Input,
  List,
  Button,
  Popconfirm,
  Modal,
} from "antd";
import {
  UserOutlined,
  CarOutlined,
  CalendarOutlined,
  BankOutlined,
  HomeOutlined,
  TeamOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  WarningOutlined,
  PhoneOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  AuditOutlined,
  TwitchOutlined,
  DownSquareOutlined,
  UpSquareOutlined,
  SaveOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  HEADERS_EXPORT,
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_CONTNO_DETAIL_SEARCH,
  POST_INSERT_NOTE_NEW,
  POST_LOAD_MEMO_NEW,
  POST_LOAN_DB2,
  POST_UPDATE_MEMO,
} from "../../../API/apiUrls";
import dayjs from "dayjs";
import TokenCheck from "../../../../hook/TokenCheck";

const { Title, Text } = Typography;

// ── ตัดบันทัดว่างซ้ำให้เหลือแค่ 1 บันทัด ──────────────
const collapseBlankLines = (text) =>
  text ? text.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n") : text;

// ==================== THEME CONFIG ====================
const THEME = {
  primary: "#1a365d",
  secondary: "#2d4a6f",
  accent: "#e67e22",
  success: "#27ae60",
  warning: "#f39c12",
  danger: "#e74c3c",
  info: "#3498db",
  light: "#f8fafc",
  dark: "#1e293b",
  gradient: "linear-gradient(135deg, #1a365d 0%, #2d4a6f 100%)",
  cardShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  borderRadius: "12px",
};

// ==================== UTILITIES ====================
const formatMoney = (value) => {
  if (!value && value !== 0) return "0";
  return Number(value).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ==================== STYLED COMPONENTS ====================
const StyledCard = ({ children, title, icon, extra, style, ...props }) => (
  <Card
    title={
      title && (
        <Space>
          {icon}
          <Text strong style={{ fontSize: 16, color: THEME.primary }}>
            {title}
          </Text>
        </Space>
      )
    }
    extra={extra}
    style={{
      boxShadow: THEME.cardShadow,
      border: "none",
      ...style,
      background: THEME.light,
      borderBottom: `2px solid ${THEME.primary}`,
      borderRadius: `${THEME.borderRadius} ${THEME.borderRadius} 0 0`,
    }}
    {...props}
  >
    {children}
  </Card>
);

const StatBox = ({
  title,
  value,
  suffix = "บาท",
  color = THEME.primary,
  icon,
}) => (
  <div
    style={{
      background: `${color}08`,
      padding: "16px 20px",
      borderRadius: "10px",
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Space align="start">
      {icon && (
        <div
          style={{
            background: color,
            padding: 8,
            borderRadius: 8,
            color: "#fff",
          }}
        >
          {icon}
        </div>
      )}
      <div>
        <Text type="secondary" style={{ fontSize: 16, display: "block" }}>
          {title}
        </Text>
        <Text strong style={{ fontSize: 18, color }}>
          {formatMoney(value)}
        </Text>
        <Text type="secondary" style={{ fontSize: 16, marginLeft: 4 }}>
          {suffix}
        </Text>
      </div>
    </Space>
  </div>
);

const InfoItem = ({ label, value, icon }) => (
  <div style={{ marginBottom: 12 }}>
    <Text type="secondary" style={{ fontSize: 16, display: "block" }}>
      {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
      {label}
    </Text>
    <Text strong style={{ fontSize: 18 }}>
      {value || "-"}
    </Text>
  </div>
);

// ==================== MAIN COMPONENT ====================
const DetailContno = ({ open, close, dataDefault }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const [signOut] = TokenCheck();
  const { TextArea } = Input;

  const [loadingJudge, setLoadingJudge] = useState(false); // loading สำหรับ judgeData
  const [loadingLoan, setLoadingLoan] = useState(false); // loading สำหรับ loanData
  const [loanData, setLoanData] = useState(null);
  const [judgeData, setJudgeData] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [memoDataOld, setMemoDataOld] = useState(null);
  const [memoDataNew, setMemoDataNew] = useState([]);
  const [showNoteOld, setShowNoteOld] = useState(true);
  const [showNoteNew, setShowNoteNew] = useState(true);
  const [editMemoOld, setEditMemoOld] = useState(true);
  const [remarkValueOld, setRemarkValueOld] = useState(null);
  const [remarkValueNew, setRemarkValueNew] = useState(null);
  console.log("dataDefault", dataDefault);

  useEffect(() => {
    if (dataDefault) {
      const payload = {
        CONTNO: dataDefault?.newContno
          ? dataDefault?.newContno
          : dataDefault.CONTNO,
      };

      loadJudgeData(payload);
      loadLoanData(payload);
      loadMemoDataOld(dataDefault?.CONTNO);
    }
  }, [dataDefault]);

  const loadLoanData = async (contno) => {
    // if (loanData) return;
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;
    setLoadingLoan(true);
    try {
      const res = await axios.post(
        POST_LOAN_DB2,
        { CONTNO: `'${useContno}'` },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res?.data?.[0]) setLoanData(res.data?.[0]);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadLoanData:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const loadJudgeData = async (payload) => {
    setLoadingJudge(true);
    try {
      const res = await axios.post(POST_CONTNO_DETAIL_SEARCH, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });

      if (res?.data) {
        setJudgeData(res.data);
        if (res.data.note) {
          setMemoDataNew(res.data.note);
        } else {
          setMemoDataNew([]);
          loadLoanData(dataDefault.CONTNO);
        }
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadJudgeData:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลคดี");
      }
      if (error?.response?.status === 401) {
        signOut();
      }
    } finally {
      setLoadingJudge(false);
    }
  };

  const loadMemoDataOld = async (contno) => {
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;
    setLoadingLoan(true);
    try {
      const res = await axios.post(POST_LOAD_MEMO_NEW, {
        contractNo: useContno,
      });
      if (res?.data) {
        const memo1 = collapseBlankLines(res.data.MEMO1);
        setMemoDataOld({ ...res.data, MEMO1: memo1 });
        setRemarkValueOld(memo1);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadMemoDataOld:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const postMemoNew = async () => {
    if (!remarkValueNew) {
      message.warning("กรุณากรอกหมายเหตุก่อนบันทึก");
      return;
    }
    const payload = {
      lawsuitId: judgeData.judge?.[0].lawsuitId,
      note: remarkValueNew,
      userId: userId,
    };
    setLoadingLoan(true);
    try {
      const res = await axios.post(POST_INSERT_NOTE_NEW, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res?.status === 200) {
        message.success("บันทึกหมายเหตุสำเร็จ");
        setRemarkValueNew("");
        await loadJudgeData(dataDefault.CONTNO);
      }
      return res;
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error postMemoNew:", error);
        message.error("เกิดข้อผิดพลาดในการบันทึกหมายเหตุ");
      }
    } finally {
      setLoadingLoan(false);
    }
  };

  const postMemoOld = async () => {
    const payload = {
      DATA_TYPE: memoDataOld.DATA_TYPE,
      CONTNO: memoDataOld.CONTNO,
      MEMO1: remarkValueOld,
    };
    setLoadingJudge(true);
    try {
      const res = await axios.post(POST_UPDATE_MEMO, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res?.status === 200) {
        message.success("บันทึกหมายเหตุสำเร็จ");
        setEditMemoOld(true);
        await loadMemoDataOld(memoDataOld.CONTNO);
      }
      return res?.data;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;
      if (status === 404) message.error("ไม่พบปลายทาง (404)");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500) message.error("เกิดข้อผิดพลาดจาก Server !");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      console.error("postMemoOld error:", error);
      throw error;
    } finally {
      setLoadingJudge(false);
    }
  };

  // ── Tab change handler ────────────────────────────────────────────────────
  const handleTabChange = (key) => {
    setActiveTab(key);
    // ถ้ากด tab 4 หรือ 5 และยังไม่มี loanData → โหลด
    if ((key === "4" || key === "5") && dataDefault?.CONTNO && !loanData) {
      loadLoanData(dataDefault.CONTNO);
    }
  };

  // ==================== RENDER: HEADER ====================
  const renderContractHeader = () => {
    const loan = loanData?.LOAN;
    const customer = loanData?.CUSTOMER;

    const FLAG_CONFIG = {
      1: {
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
        label: "ทำยอม",
        badgeColor: "#389e0d",
        badgeBg: "#d9f7be",
      },
      2: {
        background: "#fff2f0",
        border: "1px solid #ffccc7",
        label: "พิพากษา",
        badgeColor: "#cf1322",
        badgeBg: "#ffebe8",
      },
      6: {
        background: "#fff2f0",
        border: "1px solid #ffccc7",
        label: "(ส่วนต่าง)",
        badgeColor: "#cf1322",
        badgeBg: "#ffebe8",
      },
    };

    const flagStyle = FLAG_CONFIG[judgeData?.flagLoanNew] ?? FLAG_CONFIG[1];
    const lawStyle = judgeData?.judge?.[0]?.LAW_TYPE_ID === 6 && FLAG_CONFIG[6];

    return (
      <div
        style={{
          background: THEME.gradient,
          padding: "24px 32px",
          borderRadius: THEME.borderRadius,
          marginBottom: 24,
          color: "#fff",
        }}
      >
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={16}>
            <Space direction="vertical" size={4}>
              <Title level={3} style={{ color: "#fff", margin: "8px 0" }}>
                <FileTextOutlined style={{ marginRight: 12 }} />
                {loan?.CONTNO || memoDataOld?.CONTNO}
                {(judgeData?.loanNew?.NEWCONTNO ||
                  judgeData?.judge?.[0]?.newContno) &&
                  " ➡️ " +
                    (judgeData?.loanNew?.NEWCONTNO ||
                      judgeData?.judge?.[0]?.newContno)}{" "}
                <span
                  style={{
                    background: flagStyle.badgeBg,
                    color: flagStyle.badgeColor,
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontSize: 20,
                    fontWeight: 500,
                  }}
                >
                  {" "}
                  {flagStyle.label}
                  {lawStyle.label}
                </span>
              </Title>
              <Space size="large">
                <Text copyable style={{ color: "rgba(255,255,255,0.85)" }}>
                  <UserOutlined style={{ marginRight: 6 }} />
                  {customer?.SNAM}
                  {customer?.NAME1} {customer?.NAME2}
                </Text>
                <Text copyable style={{ color: "rgba(255,255,255,0.85)" }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  {customer?.CUSCOD}
                </Text>
              </Space>
            </Space>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: "right" }}>
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.7)" }}>
                  สถานะสัญญา
                </Text>
              }
              value={
                judgeData.judge
                  ? judgeData.judge?.[0]?.statusDescription
                  : judgeData?.statusDescription || "ไม่มีสถานะ"
              }
              valueStyle={{ color: "#fff", fontSize: 28 }}
            />
          </Col>
        </Row>
      </div>
    );
  };
  // ==================== TAB 1: ข้อมูลหลังพิพากษา ====================
  const renderJudgementInfo = () => {
    const judges = judgeData?.judge || [];

    if (judges?.length === 0) {
      return (
        <StyledCard>
          <Empty description="ยังไม่มีข้อมูลหลังพิพากษา" />
        </StyledCard>
      );
    }

    const groupedJudges = judges?.reduce((acc, judge) => {
      const key = judge.judgeNumber;
      if (!acc[key]) acc[key] = [];
      acc[key].push(judge);
      return acc;
    }, {});

    const judgeNumbers = Object.keys(groupedJudges).sort((a, b) => a - b);

    const renderJudgeContent = (judgeNum) => {
      const judgesInGroup = groupedJudges[judgeNum];
      const firstJudge = judgesInGroup?.[0];

      const totalJudgement =
        Number(firstJudge?.judgement || 0) +
        Number(firstJudge?.AROTHR || 0) +
        Number(firstJudge?.judgementInterest || 0) +
        Number(firstJudge?.costOfUselessness || 0);

      const totalPaid = (judgeData?.chqtran ?? []).reduce(
        (sum, item) => sum + parseFloat(item.PAYAMT || 0),
        0,
      );

      const totalPaidArothr = (judgeData?.chqtran_arothr ?? []).reduce(
        (sum, item) => sum + parseFloat(item.PAYAMT || 0),
        0,
      );

      const totalPaidAll = parseFloat(totalPaid + totalPaidArothr);
      const paymentProgress =
        totalJudgement > 0 ? (totalPaidAll / totalJudgement) * 100 : 0;

      return (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.danger}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ค่าชดใช้แทน
                </Text>
                <Title
                  level={3}
                  style={{ margin: "8px 0", color: THEME.danger }}
                >
                  {formatMoney(firstJudge?.judgement)}
                </Title>
                <Space direction="vertical" size={0}>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ต้นเงิน: {formatMoney(firstJudge?.judgementLack)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ดอกเบี้ย: {firstJudge?.interestRate || 0}% /ปี
                  </Text>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.warning}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ค่าขาดประโยชน์
                </Text>
                <Title
                  level={3}
                  style={{ margin: "8px 0", color: THEME.warning }}
                >
                  {formatMoney(firstJudge?.lackOfBenefits)}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ดอกเบี้ย: {firstJudge?.costRate || 0}% /ปี
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.info}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ลูกหนี้อื่นๆ
                </Text>
                <Title level={3} style={{ margin: "8px 0", color: THEME.info }}>
                  {formatMoney(firstJudge?.AROTHR)}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  บาท
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div
                style={{
                  textAlign: "center",
                  padding: 20,
                  background: `${THEME.accent}10`,
                  borderRadius: 10,
                  height: "100%",
                }}
              >
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ดอกเบี้ยก่อนฟ้อง
                </Text>
                <Title
                  level={3}
                  style={{ margin: "8px 0", color: THEME.accent }}
                >
                  {formatMoney(firstJudge?.judgementInterest)}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  บาท
                </Text>
              </div>
            </Col>
          </Row>

          <StyledCard style={{ marginBottom: 16 }}>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #f6ffed 0%, #fff 100%)",
                    border: "1px solid #b7eb8f",
                    borderRadius: 12,
                    padding: "16px 20px",
                  }}
                >
                  <Space style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 18 }}>💳</span>
                    <Text strong style={{ fontSize: 16 }}>
                      ความคืบหน้าการชำระ
                    </Text>
                    <Tag
                      color={paymentProgress >= 100 ? "success" : "processing"}
                      style={{ borderRadius: 10 }}
                    >
                      {Number(paymentProgress ? paymentProgress.toFixed(1) : 0)}
                      %
                    </Tag>
                  </Space>
                  <Progress
                    percent={Number(
                      paymentProgress ? paymentProgress.toFixed(1) : 0,
                    )}
                    strokeColor={{ "0%": THEME.success, "100%": "#52c41a" }}
                    trailColor="#e6f7e6"
                    size={{ width: "100%", height: 12 }}
                    style={{ marginBottom: 12 }}
                  />
                  <Row gutter={[8, 0]}>
                    <Col span={12}>
                      <div
                        style={{
                          background: "#fff",
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: "6px 12px",
                          textAlign: "center",
                        }}
                      >
                        <Text
                          type="secondary"
                          style={{ fontSize: 16, display: "block" }}
                        >
                          ชำระแล้ว
                        </Text>
                        <Text
                          strong
                          style={{ color: THEME.success, fontSize: 18 }}
                        >
                          {formatMoney(totalPaidAll)}{" "}
                          <span
                            style={{
                              color: "#aaa",
                              fontSize: 14,
                              fontWeight: 400,
                            }}
                          >
                            บาท
                          </span>
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div
                        style={{
                          background: "#fff",
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: "6px 12px",
                          textAlign: "center",
                        }}
                      >
                        <Text
                          type="secondary"
                          style={{ fontSize: 16, display: "block" }}
                        >
                          คงเหลือ
                        </Text>
                        <Text
                          strong
                          style={{ color: THEME.danger, fontSize: 18 }}
                        >
                          {formatMoney(totalJudgement - totalPaidAll)}{" "}
                          <span
                            style={{
                              color: "#aaa",
                              fontSize: 14,
                              fontWeight: 400,
                            }}
                          >
                            บาท
                          </span>
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #e6f4ff 0%, #fff 100%)",
                    border: "1px solid #91caff",
                    borderRadius: 12,
                    padding: "16px 20px",
                    height: "100%",
                  }}
                >
                  <Space style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 18 }}>📅</span>
                    <Text strong style={{ fontSize: 16 }}>
                      ข้อมูลดอกเบี้ย
                    </Text>
                  </Space>
                  <Row gutter={[10, 10]}>
                    {[
                      {
                        icon: "📌",
                        label: "วันเริ่มคิดดอกเบี้ย (เงินต้น)",
                        value: formatDate(firstJudge?.firstDate),
                        color: THEME.primary,
                      },
                      {
                        icon: "📌",
                        label: "วันเริ่มคิดดอกเบี้ย (ค่าขาดประโยชน์)",
                        value: formatDate(firstJudge?.costFirstDate),
                        color: THEME.warning,
                      },
                      {
                        icon: "📊",
                        label: "อัตราดอกเบี้ย (เงินต้น)",
                        value: `${firstJudge?.interestRate || 0}% ต่อปี`,
                        color: THEME.primary,
                      },
                      {
                        icon: "📊",
                        label: "อัตราดอกเบี้ย (ค่าขาดประโยชน์)",
                        value: `${firstJudge?.costRate || 0}% ต่อปี`,
                        color: THEME.warning,
                      },
                    ].map(({ icon, label, value, color }) => (
                      <Col span={12} key={label}>
                        <div
                          style={{
                            background: "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: "8px 10px",
                            height: "100%",
                          }}
                        >
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 16,
                              display: "block",
                              marginBottom: 2,
                            }}
                          >
                            {icon} {label}
                          </Text>
                          <Text strong style={{ color, fontSize: 18 }}>
                            {value || "-"}
                          </Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          </StyledCard>

          {judgeData?.chqtran?.length > 0 && (
            <>
              {judgeData?.loanNew ? (
                <StyledCard
                  title="ตารางการรับชำระรวม(ทำยอม)"
                  icon={
                    <DollarOutlined
                      style={{ color: THEME.primary, fontSize: 18 }}
                    />
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Table
                    dataSource={judgeData.chqtran}
                    columns={[
                      {
                        title: "ลำดับ",
                        key: "index",
                        width: 60,
                        align: "center",
                        render: (_, __, idx) => idx + 1,
                      },
                      {
                        title: "วันที่ชำระ",
                        dataIndex: "PAYDT",
                        key: "PAYDT",
                        render: (val) => formatDate(val),
                      },
                      {
                        title: "ชื่อ-นามสกุล",
                        key: "name",
                        render: (_, r) => (
                          <Text>
                            {r.SNAM}
                            {r.NAME1} {r.NAME2}
                          </Text>
                        ),
                      },
                      { title: "รายการ", dataIndex: "FORDESC", key: "FORDESC" },
                      {
                        title: "เลขที่ใบเสร็จ",
                        dataIndex: "TMBILL",
                        key: "TMBILL",
                        render: (val) => (
                          <Text style={{ fontFamily: "monospace" }}>{val}</Text>
                        ),
                      },
                      {
                        title: "จำนวนเงิน",
                        dataIndex: "NETPAY",
                        key: "NETPAY",
                        align: "right",
                        render: (val) => (
                          <Text strong style={{ color: THEME.success }}>
                            {formatMoney(val)} บาท
                          </Text>
                        ),
                      },
                      {
                        title: "ผู้รับ",
                        dataIndex: "INPUT_BY",
                        key: "INPUT_BY",
                        align: "center",
                        render: (val) => <Tag color="blue">{val}</Tag>,
                      },
                    ]}
                    rowKey="wctId"
                    pagination={{
                      defaultPageSize: 5,
                      showSizeChanger: true,
                      pageSizeOptions: ["5", "10", "20", "50", "100"],
                    }}
                    size="middle"
                    scroll={{ x: 800 }}
                    summary={(data) => {
                      const totalNetpay = data?.reduce(
                        (sum, r) => sum + Number(r.NETPAY || 0),
                        0,
                      );
                      return (
                        <Table.Summary.Row style={{ background: THEME.light }}>
                          <Table.Summary.Cell colSpan={4}>
                            <Text strong>รวมทั้งหมด</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong style={{ color: THEME.success }}>
                              {formatMoney(totalNetpay)} บาท
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </StyledCard>
              ) : (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  {judgeData?.chqtran?.filter(
                    (item) => item.FORDESC === "ค่าแทน",
                  )?.length > 0 && (
                    <Col xs={24} lg={12}>
                      <StyledCard
                        title="ตารางการรับชำระรวม - ค่าแทน"
                        icon={
                          <DollarOutlined
                            style={{ color: THEME.danger, fontSize: 18 }}
                          />
                        }
                        style={{ height: "100%" }}
                      >
                        <Table
                          dataSource={judgeData?.chqtran?.filter(
                            (item) => item.FORDESC === "ค่าแทน",
                          )}
                          columns={[
                            {
                              title: "#",
                              key: "index",
                              width: 10,
                              align: "center",
                              render: (_, __, idx) => idx + 1,
                            },
                            {
                              title: "วันที่ชำระ",
                              dataIndex: "PAYDT",
                              key: "PAYDT",
                              width: 40,
                              render: (val) => formatDate(val),
                            },
                            {
                              title: "ชื่อ-นามสกุล",
                              key: "name",
                              ellipsis: true,
                              width: 70,
                              render: (_, r) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {r.SNAM}
                                  {r.NAME1} {r.NAME2}
                                </Text>
                              ),
                            },
                            {
                              title: "ใบเสร็จ",
                              dataIndex: "TMBILL",
                              key: "TMBILL",
                              width: 50,
                              render: (val) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {val}
                                </Text>
                              ),
                            },
                            {
                              title: "จำนวนเงิน",
                              dataIndex: "NETPAY",
                              key: "NETPAY",
                              align: "right",
                              width: 40,
                              render: (val) => (
                                <Text
                                  style={{ color: THEME.success, fontSize: 16 }}
                                >
                                  {formatMoney(val)}
                                </Text>
                              ),
                            },
                            {
                              title: "ผู้รับ",
                              dataIndex: "INPUT_BY",
                              key: "INPUT_BY",
                              align: "center",
                              width: 40,
                              render: (val) => <Tag color="blue">{val}</Tag>,
                            },
                          ]}
                          rowKey="wctId"
                          pagination={{
                            defaultPageSize: 5,
                            showSizeChanger: true,
                          }}
                          size="small"
                          summary={(data) => {
                            const t = data?.reduce(
                              (sum, r) => sum + Number(r.NETPAY || 0),
                              0,
                            );
                            return (
                              <Table.Summary.Row
                                style={{ background: THEME.light }}
                              >
                                <Table.Summary.Cell colSpan={4}>
                                  <Text strong>รวมชำระ</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                  <Text
                                    strong
                                    style={{
                                      color: THEME.success,
                                      fontSize: 16,
                                    }}
                                  >
                                    {formatMoney(t)}
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell />
                              </Table.Summary.Row>
                            );
                          }}
                        />
                      </StyledCard>
                    </Col>
                  )}
                  {judgeData?.chqtran?.filter(
                    (item) => item.FORDESC === "ค่าขาดประโยชน์",
                  )?.length > 0 && (
                    <Col xs={24} lg={12}>
                      <StyledCard
                        title="ตารางการรับชำระรวม - ค่าขาดประโยชน์"
                        icon={
                          <DollarOutlined
                            style={{ color: THEME.warning, fontSize: 18 }}
                          />
                        }
                        style={{ height: "100%" }}
                      >
                        <Table
                          dataSource={judgeData?.chqtran?.filter(
                            (item) => item.FORDESC === "ค่าขาดประโยชน์",
                          )}
                          columns={[
                            {
                              title: "#",
                              key: "index",
                              width: 10,
                              align: "center",
                              render: (_, __, idx) => idx + 1,
                            },
                            {
                              title: "วันที่ชำระ",
                              dataIndex: "PAYDT",
                              key: "PAYDT",
                              width: 40,
                              render: (val) => formatDate(val),
                            },
                            {
                              title: "ชื่อ-นามสกุล",
                              key: "name",
                              width: 70,
                              ellipsis: true,
                              render: (_, r) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {r.SNAM}
                                  {r.NAME1} {r.NAME2}
                                </Text>
                              ),
                            },
                            {
                              title: "ใบเสร็จ",
                              dataIndex: "TMBILL",
                              key: "TMBILL",
                              width: 50,
                              render: (val) => (
                                <Text
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: 14,
                                  }}
                                >
                                  {val}
                                </Text>
                              ),
                            },
                            {
                              title: "จำนวนเงิน",
                              dataIndex: "NETPAY",
                              key: "NETPAY",
                              width: 40,
                              align: "right",
                              render: (val) => (
                                <Text style={{ color: THEME.success }}>
                                  {formatMoney(val)}
                                </Text>
                              ),
                            },
                            {
                              title: "ผู้รับ",
                              dataIndex: "INPUT_BY",
                              key: "INPUT_BY",
                              align: "center",
                              width: 50,
                              render: (val) => <Tag color="blue">{val}</Tag>,
                            },
                          ]}
                          rowKey="wctId"
                          pagination={{
                            defaultPageSize: 5,
                            showSizeChanger: true,
                          }}
                          size="small"
                          summary={(data) => {
                            const t = data?.reduce(
                              (sum, r) => sum + Number(r.NETPAY || 0),
                              0,
                            );
                            return (
                              <Table.Summary.Row
                                style={{ background: THEME.light }}
                              >
                                <Table.Summary.Cell colSpan={4}>
                                  <Text strong>รวมชำระ</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                  <Text
                                    strong
                                    style={{
                                      color: THEME.success,
                                      fontSize: 16,
                                    }}
                                  >
                                    {formatMoney(t)}
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell />
                              </Table.Summary.Row>
                            );
                          }}
                        />
                      </StyledCard>
                    </Col>
                  )}
                </Row>
              )}
            </>
          )}

          {judgeData?.chqtran_arothr?.length > 0 && (
            <>
              <Row style={{ marginBottom: 16 }}>
                <Col>
                  <StyledCard
                    title="ตารางการรับลูกหนี้อื่น ๆ"
                    icon={
                      <DollarOutlined
                        style={{ color: THEME.danger, fontSize: 18 }}
                      />
                    }
                    style={{ height: "100%" }}
                  >
                    <Table
                      dataSource={judgeData?.chqtran_arothr}
                      columns={[
                        {
                          title: "#",
                          key: "index",
                          width: 10,
                          align: "center",
                          render: (_, __, idx) => idx + 1,
                        },
                        {
                          title: "วันที่ชำระ",
                          dataIndex: "PAYDT",
                          key: "PAYDT",
                          width: 40,
                          render: (val) => formatDate(val),
                        },
                        {
                          title: "ชื่อ-นามสกุล",
                          key: "name",
                          ellipsis: true,
                          width: 70,
                          render: (_, r) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {r.SNAM}
                              {r.NAME1} {r.NAME2}
                            </Text>
                          ),
                        },
                        {
                          title: "ใบเสร็จ",
                          dataIndex: "TMBILL",
                          key: "TMBILL",
                          width: 30,
                          render: (val) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {val}
                            </Text>
                          ),
                        },
                        {
                          title: "ประเภทจ่าย",
                          dataIndex: "PAYTYP",
                          key: "PAYTYP",
                          width: 50,
                          align: "center",
                          render: (val) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {val === "01" ? "เงินสก" : "โอน"}
                            </Text>
                          ),
                        },
                        {
                          title: "ค่า",
                          dataIndex: "FORDESC",
                          key: "FORDESC",
                          width: 30,
                          align: "center",
                          render: (val) => (
                            <Text
                              style={{
                                fontFamily: "monospace",
                                fontSize: 14,
                              }}
                            >
                              {val}
                            </Text>
                          ),
                        },
                        {
                          title: "จำนวนเงิน",
                          dataIndex: "NETPAY",
                          key: "NETPAY",
                          align: "right",
                          width: 40,
                          render: (val) => (
                            <Text
                              style={{ color: THEME.success, fontSize: 16 }}
                            >
                              {formatMoney(val)}
                            </Text>
                          ),
                        },
                        {
                          title: "ผู้รับ",
                          dataIndex: "INPUT_BY",
                          key: "INPUT_BY",
                          align: "center",
                          width: 40,
                          render: (val) => <Tag color="blue">{val}</Tag>,
                        },
                      ]}
                      rowKey="wctId"
                      pagination={{
                        defaultPageSize: 5,
                        showSizeChanger: true,
                      }}
                      size="small"
                      summary={(data) => {
                        const t = data?.reduce(
                          (sum, r) => sum + Number(r.NETPAY || 0),
                          0,
                        );
                        return (
                          <Table.Summary.Row
                            style={{ background: THEME.light }}
                          >
                            <Table.Summary.Cell colSpan={6}>
                              <Text strong>รวมชำระ</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="right">
                              <Text
                                strong
                                style={{
                                  color: THEME.success,
                                  fontSize: 16,
                                }}
                              >
                                {formatMoney(t)}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell />
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </StyledCard>
                </Col>
              </Row>
            </>
          )}

          <StyledCard
            title={`รายละเอียดจำเลยในคำพิพากษานี้ (${judgesInGroup?.length} คน)`}
            icon={
              <TeamOutlined style={{ color: THEME.primary, fontSize: 18 }} />
            }
          >
            <Tabs
              type="card"
              items={judgesInGroup?.map((defendant) => ({
                key: String(defendant.idJudgeDef),
                label: (
                  <Space>
                    <Avatar
                      size={24}
                      style={{ background: THEME.gradient, fontSize: 14 }}
                    >
                      {defendant.defendantNumber}
                    </Avatar>
                    {defendant.SNAM}
                    {defendant.NAME1} {defendant.NAME2}
                  </Space>
                ),
                children: (
                  <div
                    style={{
                      padding: 20,
                      background: THEME.light,
                      borderRadius: 10,
                      borderLeft: `4px solid ${THEME.primary}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      <Avatar size={56} style={{ background: THEME.gradient }}>
                        {defendant.defendantNumber}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: 18 }}>
                          {defendant.SNAM}
                          {defendant.NAME1} {defendant.NAME2}
                        </Text>
                        <br />
                        <Space size={4}>
                          <Tag color="geekblue">
                            คำพิพากษาที่ {defendant.judgeNumber}
                          </Tag>
                          <Tag color="blue">
                            จำเลยที่ {defendant.defendantNumber}
                          </Tag>
                          <Tag color={defendant.acquitted ? "green" : "orange"}>
                            {judgeData.flagLoanNew === 1 ? "ทำยอม" : "พิพากษา"}
                          </Tag>
                        </Space>
                      </div>
                    </div>
                    <Row gutter={[12, 8]}>
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          <IdcardOutlined style={{ marginRight: 6 }} />
                          เลขบัตรประชาชน/ทะเบียนนิติบุคคล
                        </Text>
                        <br />
                        <Text
                          copyable
                          strong
                          style={{ fontFamily: "monospace", fontSize: 18 }}
                        >
                          {defendant.CUSCOD}
                        </Text>
                      </Col>
                    </Row>
                    <Divider style={{ margin: "12px 0" }} />
                    <Row
                      gutter={[12, 12]}
                      style={{ marginLeft: 8, marginTop: 8 }}
                    >
                      {[
                        {
                          label: "ยอดชดใช้แทน",
                          value: formatMoney(defendant.judgement),
                          color: THEME.danger,
                          icon: "💰",
                        },
                        {
                          label: "ค่าขาดประโยชน์",
                          value: formatMoney(defendant.lackOfBenefits),
                          color: THEME.primary,
                          icon: "📉",
                        },
                        {
                          label: "ชำระแล้ว",
                          value: formatMoney(defendant.PAY),
                          color: THEME.success,
                          icon: "✅",
                        },
                        {
                          label: "ค่าขาดประโยชน์รายเดือน",
                          value: formatMoney(
                            defendant.costOfUselesenessPerMonth,
                          ),
                          color: THEME.warning,
                          icon: "📆",
                        },
                        {
                          label: "ค่าติดตาม",
                          value: formatMoney(defendant.trackingFee),
                          color: THEME.primary,
                          icon: "🔍",
                        },
                        {
                          label: "ระยะเวลาค่าขาดประโยชน์",
                          value: defendant.costOfUselesenessMonth,
                          color: THEME.warning,
                          icon: "📆",
                        },
                        {
                          label: "ค่าทนาย",
                          value: formatMoney(defendant.attorneyFees),
                          color: THEME.primary,
                          icon: "⚖️",
                        },
                        {
                          label: "ค่าธรรมเนียม",
                          value: formatMoney(defendant.fee),
                          color: THEME.primary,
                          icon: "🧾",
                        },
                      ].map(({ label, value, color, icon }) => (
                        <Col span={12} key={label}>
                          <div
                            style={{
                              background: "#fff",
                              border: "1px solid #f0f0f0",
                              borderRadius: 8,
                              padding: "8px 12px",
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span style={{ fontSize: 18 }}>{icon}</span>
                            <div>
                              <Text
                                type="secondary"
                                style={{ fontSize: 16, display: "block" }}
                              >
                                {label}
                              </Text>
                              <Text strong style={{ color, fontSize: 18 }}>
                                {value}{" "}
                                <span
                                  style={{
                                    color: "#aaa",
                                    fontWeight: 400,
                                    fontSize: 12,
                                  }}
                                >
                                  {value === defendant.costOfUselesenessMonth
                                    ? "เดือน"
                                    : "บาท"}
                                </span>
                              </Text>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <Divider style={{ margin: "12px 0" }} />
                    <Table
                      dataSource={judgeData?.chqtran?.filter(
                        (item) => item?.CUSTOMER_ID === defendant?.CUSTOMER_ID,
                      )}
                      columns={[
                        {
                          title: "#",
                          key: "index",
                          align: "center",
                          render: (_, __, idx) => idx + 1,
                        },
                        {
                          title: "วันที่ชำระ",
                          dataIndex: "PAYDT",
                          key: "PAYDT",
                          align: "center",
                          render: (val) => formatDate(val),
                        },
                        {
                          title: "ใบเสร็จ",
                          dataIndex: "TMBILL",
                          key: "TMBILL",
                          align: "center",
                          render: (val) => (
                            <Text
                              style={{ fontFamily: "monospace", fontSize: 16 }}
                            >
                              {val}
                            </Text>
                          ),
                        },
                        {
                          title: "ประเภทจ่าย",
                          dataIndex: "FORDESC",
                          key: "FORDESC",
                          align: "center",
                        },
                        {
                          title: "จำนวนเงิน",
                          dataIndex: "NETPAY",
                          key: "NETPAY",
                          align: "right",
                          render: (val) => (
                            <Text
                              style={{ color: THEME.success, fontSize: 16 }}
                            >
                              {formatMoney(val)}
                            </Text>
                          ),
                        },
                        {
                          title: "ผู้รับ",
                          dataIndex: "INPUT_BY",
                          key: "INPUT_BY",
                          align: "center",
                          render: (val) => <Tag color="blue">{val}</Tag>,
                        },
                      ]}
                      rowKey="wctId"
                      pagination={{ defaultPageSize: 5, showSizeChanger: true }}
                      size="small"
                      scroll={{ x: 500 }}
                      summary={(data) => {
                        const t = data?.reduce(
                          (sum, r) => sum + Number(r.NETPAY || 0),
                          0,
                        );
                        return (
                          <Table.Summary.Row
                            style={{ background: THEME.light }}
                          >
                            <Table.Summary.Cell colSpan={4}>
                              <Text strong>รวมชำระ</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="right">
                              <Text
                                strong
                                style={{ color: THEME.success, fontSize: 18 }}
                              >
                                {formatMoney(t)}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell />
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </div>
                ),
              }))}
            />
          </StyledCard>
        </div>
      );
    };

    const judgeTabs = judgeNumbers
      .filter((judgeNum) => judgeNum !== "0") // ✅ กรอง key "0" ออก
      .map((judgeNum) => ({
        key: judgeNum,
        label: (
          <span>
            <SafetyCertificateOutlined />
            คำพิพากษาที่ {judgeNum}
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {groupedJudges[judgeNum]?.length} คน
            </Tag>
          </span>
        ),
        children: renderJudgeContent(judgeNum),
      }));

    if (!judgeTabs?.length) return null;

    return (
      <Tabs
        type="card"
        size="middle"
        items={judgeTabs}
        style={{ marginBottom: 16 }}
      />
    );
  };

  // ==================== TAB 2: ข้อมูลสัญญาใหม่ ====================
  const renderLoanNewInfo = () => {
    const loan = judgeData?.loanNew;
    if (!loan) return <Empty />;
    return (
      <StyledCard
        title="ข้อมูลสัญญา(ใหม่)"
        icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดจัดทั้งหมด"
              value={loan?.LOAN_NEW_TONBALANCE}
              color={THEME.primary}
              icon={<DollarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ชำระแล้ว"
              value={loan?.LOAN_NEW_SMPAY}
              color={THEME.success}
              icon={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ค่างวด"
              value={loan?.TOT_UPAY}
              color={THEME.info}
              icon={<WarningOutlined />}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "20px 0" }} />
        <Row gutter={[32, 16]}>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="วันทำสัญญา"
              value={formatDate(loan?.SDATE)}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดแรก" value={formatDate(loan?.FDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดสุดท้าย" value={formatDate(loan?.LDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="จำนวนงวด" value={`${loan?.T_NOPAY || 0} งวด`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="งวดละ"
              value={`${formatMoney(loan?.TOT_UPAY)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="ชำระล่าสุด" value={formatDate(loan?.LPAYD)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ยอดชำระล่าสุด"
              value={`${formatMoney(loan?.LPAYA)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="สัญญาเก่าคือ" value={`${loan?.ISSUNO || "-"}`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="สัญญาใหม่"
              value={`${judgeData?.loanNew?.NEWCONTNO || "-"}`}
            />
          </Col>
        </Row>
      </StyledCard>
    );
  };
  const renderInvestigationInfo = () => {
    const asset = judgeData?.asset ?? [];

    // garno=0 และ relationCustomerId=null → ผู้กู้หลัก
    const mainBorrower = asset.filter(
      (r) => r.garno === 0 && r.relationCustomerId === null,
    );

    // garno=0 แต่มี relationCustomerId → คู่สมรสของผู้ค้ำ
    const spouseMap = {};
    asset
      .filter((r) => r.garno === 0 && r.relationCustomerId !== null)
      .forEach((r) => {
        spouseMap[r.relationCustomerId] = r;
      });

    // guarantors เรียงตาม garno แล้ว insert คู่สมรสหลังแต่ละคน
    const guarantorsSorted = asset
      .filter((r) => r.garno > 0)
      .sort((a, b) => a.garno - b.garno);

    const guarantors = [];
    guarantorsSorted.forEach((g) => {
      guarantors.push(g);
      const spouse = spouseMap[g.customerId];
      if (spouse) guarantors.push(spouse);
    });

    const PersonCard = ({ record }) => {
      const hasAsset = record.assetLand?.length > 0;
      const isSpouseOfGuarantor =
        record.garno === 0 && record.relationCustomerId !== null;

      return (
        <StyledCard
          title={
            <Space>
              <UserOutlined
                style={{
                  color:
                    record.garno === 0 && !isSpouseOfGuarantor
                      ? THEME.primary
                      : THEME.warning,
                }}
              />
              <Text strong style={{ fontSize: 14 }}>
                {record.garno === 0 && !isSpouseOfGuarantor
                  ? `ผู้กู้หลัก`
                  : record.garno === 0 && isSpouseOfGuarantor
                    ? `คู่สมรสผู้ค้ำ`
                    : `ผู้ค้ำประกัน ${record.garno}`}
              </Text>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <div
            style={{
              background: hasAsset
                ? "linear-gradient(135deg, #f0f7ff 0%, #f8fafc 100%)"
                : "linear-gradient(135deg, #fff0f0 0%, #fff8f8 100%)",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 14,
              border: hasAsset ? "1px solid #dbeafe" : "1px solid #fecaca",
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Space align="start">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: hasAsset ? THEME.primary : "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <UserOutlined style={{ color: "#fff", fontSize: 16 }} />
                  </div>
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: 14, display: "block" }}
                    >
                      ชื่อ-นามสกุล
                    </Text>
                    <Text
                      strong
                      style={{
                        fontSize: 16,
                        color: hasAsset ? undefined : "#ef4444",
                      }}
                    >
                      {`${record.SNAM}${record.NAME1} ${record.NAME2}`}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space align="start">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: THEME.info,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IdcardOutlined style={{ color: "#fff", fontSize: 16 }} />
                  </div>
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: 14, display: "block" }}
                    >
                      เลขบัตรประชาชน
                    </Text>
                    <Text
                      strong
                      style={{ fontSize: 16, letterSpacing: "0.5px" }}
                    >
                      {record.CUSCOD ?? "-"}
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </div>
          <div
            style={{
              borderRadius: 10,
              border: "1px solid #e8ecf0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>🏞️</span>
              <Text strong style={{ color: "#fff", fontSize: 14 }}>
                ข้อมูลที่ดิน
              </Text>
              <Tag
                color={hasAsset ? "blue" : "red"}
                style={{ marginLeft: "auto", borderRadius: 20 }}
              >
                {record.assetLand?.length ?? 0} แปลง
              </Tag>
            </div>
            <div style={{ padding: "12px 14px", background: "#f8fafc" }}>
              {!hasAsset ? (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    ไม่มีข้อมูลที่ดิน
                  </Text>
                </div>
              ) : (
                record.assetLand.map((land, i) => (
                  <div
                    key={land.landId}
                    style={{
                      background: "#fff",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginBottom: i < record.assetLand.length - 1 ? 10 : 0,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          background: THEME.primary,
                          color: "#fff",
                          borderRadius: 20,
                          padding: "1px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        แปลงที่ {i + 1}
                      </div>
                      <Tag
                        color={land.typeLand === 1 ? "geekblue" : "default"}
                        style={{ fontSize: 11 }}
                      >
                        {land.typeLand === 1 ? "โฉนด" : "อื่นๆ"}
                      </Tag>
                    </div>
                    <Row gutter={[8, 6]}>
                      {[
                        { label: "เลขที่", value: land.numberLand, icon: "🏛️" },
                        {
                          label: "ตำบล",
                          value: land.subDistDesc ?? land.subdistrict,
                          icon: "🗾",
                        },
                        {
                          label: "อำเภอ",
                          value: land.distDesc ?? land.district,
                          icon: "🏛️",
                        },
                        {
                          label: "จังหวัด",
                          value: land.provDesc ?? land.province,
                          icon: "🗺️",
                        },
                      ].map(({ label, value, icon }) => (
                        <Col xs={12} sm={6} key={label}>
                          <div
                            style={{
                              background: "#f8fafc",
                              borderRadius: 6,
                              padding: "5px 10px",
                              border: "1px solid #e8ecf0",
                            }}
                          >
                            <Text
                              type="secondary"
                              style={{ fontSize: 14, display: "block" }}
                            >
                              {icon} {label}
                            </Text>
                            <Text style={{ fontSize: 16, fontWeight: 500 }}>
                              {value ?? "-"}
                            </Text>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))
              )}
            </div>
          </div>
        </StyledCard>
      );
    };

    if (asset?.length === 0) return <Empty />;
    return (
      <>
        {mainBorrower.map((r) => (
          <PersonCard key={r.customerId} record={r} />
        ))}
        {guarantors?.length > 0 && (
          <>
            <Divider orientation="left">
              <Text strong style={{ color: THEME.warning }}>
                ผู้ค้ำประกัน ({guarantorsSorted?.length} คน)
              </Text>
            </Divider>
            {guarantors.map((r) => (
              <PersonCard key={r.customerId} record={r} />
            ))}
          </>
        )}
      </>
    );
  };

  // ==================== TAB 4: ข้อมูลสัญญาเก่า ====================
  const renderLoanInfo = () => {
    const loan = loanData?.LOAN;
    return (
      <StyledCard
        title="ข้อมูลสัญญา(เก่า)"
        icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดจัดทั้งหมด"
              value={loan?.TOTPRC}
              color={THEME.primary}
              icon={<DollarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดจัดไม่รวมดอก"
              value={loan?.NCSHPRC}
              color={THEME.info}
              icon={<BankOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ชำระแล้ว"
              value={loan?.SMPAY}
              color={THEME.success}
              icon={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <StatBox
              title="ยอดค้างชำระ ≈"
              value={loan?.EXP_AMT}
              color={THEME.danger}
              icon={<WarningOutlined />}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "20px 0" }} />
        <Row gutter={[32, 16]}>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="วันทำสัญญา"
              value={formatDate(loan?.SDATE)}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดแรก" value={formatDate(loan?.FDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="งวดสุดท้าย" value={formatDate(loan?.LDATE)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="จำนวนงวด" value={`${loan?.T_NOPAY || 0} งวด`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="งวดละ"
              value={`${formatMoney(loan?.TOT_UPAY)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="ชำระล่าสุด" value={formatDate(loan?.LPAYD)} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ยอดชำระล่าสุด"
              value={`${formatMoney(loan?.LPAYA)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ยอดค้างชำระ ≈"
              value={`${formatMoney(loan?.EXP_AMT)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ตั้งแต่"
              value={
                <Tag color={loan?.EXP_PRD > 3 ? "red" : "orange"}>
                  ค้าง {loan?.EXP_PRD || 0} งวด ({loan?.EXP_FRM}-{loan?.EXP_TO})
                </Tag>
              }
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="ค่าอื่นๆค้างชำระ"
              value={`${formatMoney(loan?.PAYAMTAROTHR)} บาท`}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem label="สัญญาเก่าคือ" value={`${loan?.ISSUNO || "-"}`} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <InfoItem
              label="สัญญาใหม่"
              value={`${judgeData?.loanNew?.NEWCONTNO || "-"}`}
            />
          </Col>
        </Row>
      </StyledCard>
    );
  };

  const renderMortgageInfo = () => {
    const mortgage = loanData?.MORTGAGE;
    return (
      <StyledCard
        title="ข้อมูลหลักประกัน"
        icon={<CarOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div
              style={{
                background: `linear-gradient(135deg, ${THEME.light} 0%, #e2e8f0 100%)`,
                padding: 20,
                borderRadius: 10,
                marginBottom: 16,
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ยี่ห้อ / รุ่น
                </Text>
                <Title level={4} style={{ margin: 0, color: THEME.primary }}>
                  {mortgage?.TYPE} {mortgage?.MODEL}
                </Title>
                <Text>{mortgage?.BAAB}</Text>
                <Space>
                  <Tag color="blue">ปี {mortgage?.MANUYR}</Tag>
                  <Tag color="geekblue">สี{mortgage?.COLOR}</Tag>
                </Space>
              </Space>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <InfoItem
                  label="ทะเบียนรถ"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.REGNO}
                    </Text>
                  }
                />
              </Col>
              <Col span={12}>
                <InfoItem
                  label="จังหวัด"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.DORECV}
                    </Text>
                  }
                />
              </Col>
              <Col span={24}>
                <InfoItem
                  label="เลขตัวถัง"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.STRNO}
                    </Text>
                  }
                />
              </Col>
              <Col span={24}>
                <InfoItem
                  label="เลขเครื่อง"
                  value={
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {mortgage?.ENGNO}
                    </Text>
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </StyledCard>
    );
  };

  // ==================== TAB 5: ข้อมูลบุคคล ====================
  const renderCustomerInfo = () => {
    const customer = loanData?.CUSTOMER;
    const address = customer?.ADDRESS?.[0];
    return (
      <StyledCard
        title="ผู้เช่าซื้อ"
        icon={<UserOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "20px",
                background: THEME.light,
                borderRadius: 10,
                marginBottom: 16,
              }}
            >
              <Avatar
                size={72}
                style={{ background: THEME.gradient }}
                icon={<UserOutlined />}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {customer?.SNAM}
                  {customer?.NAME1} {customer?.NAME2}
                </Title>
              </div>
            </div>
            <Row gutter={[16, 12]}>
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  เลขบัตรประชาชน
                </Text>
                <br />
                <Text copyable strong>
                  {customer?.CUSCOD}
                </Text>
              </Col>
              <Col span={12}>
                <InfoItem
                  label="วันเกิด"
                  value={formatDate(customer?.BIRTHDT)}
                  icon={<CalendarOutlined />}
                />
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  อาชีพ
                </Text>
                <br />
                <Text copyable strong>
                  {customer?.OCCUP || "-"}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  ที่ทำงาน
                </Text>
                <br />
                <Text copyable strong style={{ fontSize: 16 }}>
                  {customer?.OFFIC || "-"}
                </Text>
              </Col>
            </Row>
          </Col>
          <Col xs={24} lg={12}>
            <div
              style={{
                padding: 20,
                background: `${THEME.info}08`,
                borderRadius: 10,
                borderLeft: `4px solid ${THEME.info}`,
                height: "100%",
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                <Text strong style={{ color: THEME.info }}>
                  <HomeOutlined style={{ marginRight: 8 }} />
                  ที่อยู่ตามทะเบียน
                </Text>
                <Text copyable style={{ fontSize: 16 }}>
                  {address
                    ? `${address.ADDR1} ${address.ADDR2} ${address.TUMB} ${address.AUMPDES} ${address.PROVDES} ${address.ZIP}`
                    : "-"}
                </Text>
                <Divider style={{ margin: "12px 0" }} />
                <Space>
                  <PhoneOutlined style={{ color: THEME.primary }} />
                  <Text strong copyable>
                    {address?.TELP || "-"}
                  </Text>
                </Space>
              </Space>
            </div>
          </Col>
        </Row>
      </StyledCard>
    );
  };

  const renderGuarantors = () => {
    const guarantors = loanData?.GUARANTORS || [];
    return (
      <StyledCard
        title={`ผู้ค้ำประกัน (${guarantors?.length} คน)`}
        icon={<TeamOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
      >
        {guarantors?.length === 0 ? (
          <Empty description="ไม่มีข้อมูลผู้ค้ำประกัน" />
        ) : (
          <Row gutter={[16, 16]}>
            {guarantors?.map((g, idx) => {
              const gAddress = g.ADDRESS?.[0];
              return (
                <Col xs={24} lg={12} key={idx}>
                  <div
                    style={{
                      padding: 20,
                      background: THEME.light,
                      borderRadius: 10,
                      borderLeft: `4px solid ${THEME.accent}`,
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      <Avatar size={56} style={{ background: THEME.accent }}>
                        {g.NAME1?.charAt(0)}
                      </Avatar>
                      <div>
                        <Text strong style={{ fontSize: 18 }}>
                          {g.SNAM}
                          {g.NAME1} {g.NAME2}
                        </Text>
                        <br />
                        <Space size={4}>
                          <Tag color="orange">ลำดับที่ {g.GARNO}</Tag>
                          <Tag>{g.RELATN}</Tag>
                        </Space>
                      </div>
                    </div>
                    <Row gutter={[12, 8]}>
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          <IdcardOutlined style={{ marginRight: 6 }} />
                          เลขบัตรประชาชน
                        </Text>
                        <br />
                        <Text copyable strong>
                          {g.CUSCOD}
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          วันเกิด
                        </Text>
                        <br />
                        <Text style={{ fontSize: 18 }}>
                          {formatDate(g.BIRTHDT)}
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          อาชีพ
                        </Text>
                        <br />
                        <Text style={{ fontSize: 18 }}>{g?.OCCUP || "-"}</Text>
                      </Col>
                      <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                          ที่ทำงาน
                        </Text>
                        <br />
                        <Text style={{ fontSize: 16 }} copyable>
                          {g.OFFIC || "-"}
                        </Text>
                      </Col>
                    </Row>
                    <Divider style={{ margin: "12px 0" }} />
                    <div
                      style={{
                        padding: 12,
                        background: "#fff",
                        borderRadius: 8,
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 16 }}>
                        <HomeOutlined style={{ marginRight: 6 }} />
                        ที่อยู่
                      </Text>
                      <br />
                      <Text style={{ fontSize: 16 }} copyable>
                        {gAddress
                          ? `${gAddress.ADDR1} ${gAddress.ADDR2} ${gAddress.TUMB} ${gAddress.AUMPDES} ${gAddress.PROVDES} ${gAddress.ZIP}`
                          : "-"}
                      </Text>
                      <br />
                      <Space style={{ marginTop: 8 }}>
                        <PhoneOutlined style={{ color: THEME.success }} />
                        <Text strong copyable>
                          {gAddress?.TELP || "-"}
                        </Text>
                      </Space>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </StyledCard>
    );
  };

  const renderMemo = () => {
    return (
      <>
        <Divider orientation="left">
          <Space>
            <Typography.Text
              strong
              onClick={() => setShowNoteOld(!showNoteOld)}
              style={{ cursor: "pointer" }}
            >
              ข้อมูลจาก "Smart-dev"
            </Typography.Text>
            <Tag
              color="blue"
              onClick={() => setShowNoteOld(!showNoteOld)}
              style={{ cursor: "pointer" }}
            >
              {!showNoteOld ? <DownSquareOutlined /> : <UpSquareOutlined />}
            </Tag>
          </Space>
        </Divider>

        {showNoteOld && memoDataOld && (
          <Card style={{ marginBottom: 8 }}>
            <Input.TextArea
              readOnly={editMemoOld}
              value={memoDataOld?.MEMO1}
              onChange={(e) =>
                setMemoDataOld({ ...memoDataOld, MEMO1: e.target.value })
              }
              autoSize
              style={{
                border: !editMemoOld ? "1.5px solid #ff4d4f" : undefined, // ✅ สีแดงเมื่อแก้ไข
                borderRadius: 6,
                transition: "border 0.3s",
                backgroundColor: !editMemoOld ? "#fff5f5" : "#fafafa", // ✅ พื้นหลังชมพูอ่อน
              }}
            />
            <Row>
              <Col span={24} style={{ textAlign: "center" }}>
                <Space style={{ marginTop: 10 }}>
                  {editMemoOld ? (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setEditMemoOld(false)}
                      disabled={![1, 2, 3, 13].includes(roleId)}
                    >
                      แก้ไข
                    </Button>
                  ) : (
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => setEditMemoOld(true)}
                    >
                      ยกเลิก
                    </Button>
                  )}
                  <Popconfirm
                    placement="topLeft"
                    title="อัพเดทข้อมูลโปรแกรม Smart dev"
                    description="กรุณาตรวจสอบข้อมูลให้เรียบร้อย !"
                    onConfirm={postMemoOld} // ✅
                    okText="ยืนยัน"
                    cancelText="ปิด"
                    okButtonProps={{ size: "large" }}
                    cancelButtonProps={{ size: "large" }}
                  >
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      disabled={editMemoOld}
                    >
                      บันทึก
                    </Button>
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        <Divider orientation="left">
          <Space>
            <Typography.Text
              strong
              onClick={() => {
                setShowNoteNew(!showNoteNew);
              }}
              style={{ cursor: "pointer" }}
            >
              ข้อมูลจาก "โปรแกรมใหม่"
            </Typography.Text>
            <Tag
              color="blue"
              onClick={() => {
                setShowNoteNew(!showNoteNew);
              }}
              style={{ cursor: "pointer" }}
            >
              {memoDataNew ? memoDataNew?.length : 0} รายการ{" "}
              {!showNoteNew ? <DownSquareOutlined /> : <UpSquareOutlined />}
            </Tag>
          </Space>
        </Divider>
        {showNoteNew && (
          <>
            <List
              style={{ marginBottom: 16, borderRadius: 8, overflow: "hidden" }}
              bordered
              dataSource={memoDataNew}
              locale={{ emptyText: "ไม่มีข้อมูลหมายเหตุ" }}
              renderItem={(item, index) => (
                <List.Item
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f0f7ff" : "#ffffff",
                    padding: "12px 16px",
                    alignItems: "flex-start",
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#1890ff" : "#096dd9",
                          fontWeight: "bold",
                        }}
                      >
                        {item?.USERNAME?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 4,
                        }}
                      >
                        <Space size={6}>
                          <Typography.Text strong style={{ color: "#1a365d" }}>
                            {item?.USERNAME}
                          </Typography.Text>
                          <Tag
                            color="blue"
                            style={{ borderRadius: 10, fontSize: 11 }}
                          >
                            {dayjs(item?.CreatedAt).format("DD/MM/YYYY HH:mm")}
                          </Tag>
                        </Space>
                        <Typography.Text
                          copyable={{
                            text: `${item?.note}\n ${dayjs(item?.CreatedAt).format("DD/MM/YYYY HH:mm")} [${item?.USERNAME}]`,
                            tooltips: ["คัดลอก", "คัดลอกแล้ว!"],
                          }}
                          style={{ fontSize: 12, color: "#888" }}
                        />
                      </div>
                    }
                    description={
                      <Typography.Text
                        style={{
                          fontSize: 13,
                          color: "#333",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.7,
                        }}
                      >
                        {item?.note}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              )}
            />
            <TextArea
              placeholder="กรอกหมายเหตุ (ถ้ามี)"
              value={remarkValueNew}
              onChange={(e) => setRemarkValueNew(e.target.value)}
              rows={5}
              showCount
              maxLength={500}
              style={{ marginBottom: "20px" }}
            />
            <Row>
              <Col span={24} style={{ textAlign: "center" }}>
                <Popconfirm
                  placement="topLeft"
                  title="อัพเดทข้อมูลโปรแกรมใหม่"
                  description="กรุณาตรวจสอบข้อมูลให้เรียบร้อย ไม่สามารถแก้ไขสิ่งที่ได้บันทึกได้!"
                  onConfirm={postMemoNew} // ✅
                  okText="ยืนยัน"
                  cancelText="ปิด"
                  okButtonProps={{ size: "large" }}
                  cancelButtonProps={{ size: "large" }}
                >
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    disabled={!remarkValueNew}
                  >
                    บันทึก
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </>
        )}
      </>
    );
  };

  // ==================== TAB ITEMS ====================
  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <AuditOutlined />
          ข้อมูลหลังพิพากษา
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <div>
            {judgeData ? (
              <>{renderJudgementInfo()}</>
            ) : (
              !judgeData && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <FileTextOutlined />
          ข้อมูลสัญญา(ใหม่)
        </span>
      ),
      children: (
        <Spin spinning={loadingJudge}>
          <div>
            {judgeData ? (
              <>{renderLoanNewInfo()}</>
            ) : (
              !judgeData && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    // {
    //   key: "3",
    //   label: (
    //     <span>
    //       <FileTextOutlined />
    //       ข้อมูลทรัพย์
    //     </span>
    //   ),
    //   children: (
    //     <Spin spinning={loadingJudge}>
    //       <div>
    //         {judgeData?.asset ? (
    //           <>{renderInvestigationInfo()}</>
    //         ) : (
    //           !judgeData?.asset && <Empty description="ไม่มีข้อมูล" />
    //         )}
    //       </div>
    //     </Spin>
    //   ),
    // },
    {
      key: "4",
      label: (
        <span>
          <FileTextOutlined />
          ข้อมูลสัญญา(เก่า)
        </span>
      ),
      children: (
        <Spin spinning={loadingLoan} tip="กำลังโหลดข้อมูลสัญญา...">
          <div>
            {loanData ? (
              <>
                {renderLoanInfo()}
                {renderMortgageInfo()}
              </>
            ) : (
              !loadingLoan && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "5",
      label: (
        <span>
          <SolutionOutlined />
          ข้อมูลบุคคล
        </span>
      ),
      children: (
        <Spin spinning={loadingLoan} tip="กำลังโหลดข้อมูลบุคคล...">
          <div>
            {loanData ? (
              <>
                {renderCustomerInfo()}
                {renderGuarantors()}
              </>
            ) : (
              !loadingLoan && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
    {
      key: "6",
      label: (
        <span>
          <TwitchOutlined />
          memo
        </span>
      ),
      children: (
        <Spin spinning={loadingLoan} tip="กำลังโหลดข้อมูลบุคคล...">
          <div>
            {memoDataOld ? (
              <>{renderMemo()}</>
            ) : (
              !memoDataOld && <Empty description="ไม่มีข้อมูล" />
            )}
          </div>
        </Spin>
      ),
    },
  ];

  // ==================== MAIN RENDER ====================
  if (!judgeData && !loadingJudge) {
    return (
      <Card
        style={{
          borderRadius: THEME.borderRadius,
          boxShadow: THEME.cardShadow,
          textAlign: "center",
          padding: 40,
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical">
              <Text strong style={{ fontSize: 16 }}>
                ไม่มีข้อมูล
              </Text>
              <Text type="secondary">กรุณาค้นหาเลขสัญญาเพื่อดูรายละเอียด</Text>
            </Space>
          }
        />
      </Card>
    );
  }

  const handleCancel = () => close(false);

  return (
    <Spin spinning={loadingJudge} size="large" tip="กำลังโหลดข้อมูล...">
      <Modal
        title={
          <span style={{ fontSize: 16, fontWeight: 400 }}>ส่ง EMS (ทำยอม)</span>
        }
        open={open}
        onCancel={handleCancel}
        width={1450}
        footer={null}
        keyboard
        maskClosable={false}
        centered
        styles={{ body: { padding: "24px 32px" } }}
      >
        <div style={{ padding: "0 0 24px" }}>
          {judgeData && renderContractHeader()}

          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            type="card"
            size="large"
            items={tabItems}
            style={{
              background: "#fff",
              padding: "16px",
              borderRadius: THEME.borderRadius,
              boxShadow: THEME.cardShadow,
            }}
          />
        </div>
      </Modal>
    </Spin>
  );
};

export default DetailContno;
